
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

interface TermsPrivacyDialogProps {
  type: 'terms' | 'privacy';
  children: React.ReactNode;
}

const TermsPrivacyDialog: React.FC<TermsPrivacyDialogProps> = ({ type, children }) => {
  const title = type === 'terms' ? 'Terms of Service' : 'Privacy Policy';
  
  return (
    <Dialog>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="mt-4 max-h-[60vh] pr-4">
          <div className="space-y-4 text-sm">
            {type === 'terms' ? (
              <TermsContent />
            ) : (
              <PrivacyContent />
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

const TermsContent = () => {
  return (
    <>
      <h2 className="text-lg font-semibold">Terms of Service</h2>
      <p>Last updated: April 14, 2025</p>
      
      <h3 className="font-medium text-base mt-4">1. Acceptance of Terms</h3>
      <p>
        By accessing and using our reseller platform, you agree to be bound by these Terms of Service. 
        If you do not agree to these terms, please do not use our services.
      </p>
      
      <h3 className="font-medium text-base mt-4">2. Reseller Rights and Responsibilities</h3>
      <p>
        As a reseller, you are granted a non-exclusive right to market and sell our services under your own brand. 
        You are responsible for all customer interactions, billing relationships, and support for your customers.
      </p>
      
      <h3 className="font-medium text-base mt-4">3. Payments and Fees</h3>
      <p>
        You agree to pay the setup fee and monthly subscription costs associated with your reseller plan. 
        All fees are non-refundable. Monthly fees will be charged automatically to your payment method on file.
      </p>
      
      <h3 className="font-medium text-base mt-4">4. Account Security</h3>
      <p>
        You are responsible for maintaining the security of your account credentials and for all activities 
        that occur under your account. Notify us immediately of any unauthorized use of your account.
      </p>
      
      <h3 className="font-medium text-base mt-4">5. Prohibited Activities</h3>
      <p>
        You agree not to use our platform for any illegal purposes, to infringe on intellectual property rights, 
        or to distribute malware or harmful content. We reserve the right to terminate accounts that violate these terms.
      </p>
      
      <h3 className="font-medium text-base mt-4">6. Service Availability</h3>
      <p>
        While we strive for 99.9% uptime, we do not guarantee uninterrupted service. We are not liable for any 
        business losses resulting from service outages or maintenance periods.
      </p>
      
      <h3 className="font-medium text-base mt-4">7. Termination</h3>
      <p>
        Either party may terminate this agreement with 30 days written notice. Upon termination, you will lose access 
        to the platform, but your customers' data will be available for export for 30 days.
      </p>
      
      <h3 className="font-medium text-base mt-4">8. Changes to Terms</h3>
      <p>
        We reserve the right to modify these terms at any time. Continued use of the service after such modifications 
        constitutes acceptance of the new terms.
      </p>
      
      <h3 className="font-medium text-base mt-4">9. Limitation of Liability</h3>
      <p>
        Our total liability for any claims under these terms shall not exceed the amounts paid by you to us in the 
        past 12 months. We are not liable for any indirect, incidental, or consequential damages.
      </p>
      
      <h3 className="font-medium text-base mt-4">10. Governing Law</h3>
      <p>
        These terms are governed by the laws of the United States and the state of Delaware, without regard to 
        its conflict of law provisions.
      </p>
    </>
  );
};

const PrivacyContent = () => {
  return (
    <>
      <h2 className="text-lg font-semibold">Privacy Policy</h2>
      <p>Last updated: April 14, 2025</p>
      
      <h3 className="font-medium text-base mt-4">1. Information We Collect</h3>
      <p>
        We collect personal information you provide when registering for our services, including your name, email, 
        company information, billing address, and payment details. We also collect usage data and information about 
        your interactions with our platform.
      </p>
      
      <h3 className="font-medium text-base mt-4">2. How We Use Your Information</h3>
      <p>
        We use your information to provide and improve our services, process payments, communicate with you, 
        and comply with legal obligations. We may also use anonymized data for analytics and service improvements.
      </p>
      
      <h3 className="font-medium text-base mt-4">3. Data Security</h3>
      <p>
        We implement appropriate security measures to protect your personal information from unauthorized access, 
        disclosure, alteration, and destruction. However, no internet transmission is completely secure.
      </p>
      
      <h3 className="font-medium text-base mt-4">4. Data Retention</h3>
      <p>
        We retain your personal information for as long as necessary to provide you with our services and as 
        required by law. You may request deletion of your data at any time, subject to legal retention requirements.
      </p>
      
      <h3 className="font-medium text-base mt-4">5. Sharing Your Information</h3>
      <p>
        We may share your information with service providers who help us deliver our services, such as payment 
        processors and hosting providers. We do not sell your personal information to third parties.
      </p>
      
      <h3 className="font-medium text-base mt-4">6. Your Rights</h3>
      <p>
        Depending on your location, you may have rights to access, correct, or delete your personal information. 
        You may also have the right to object to or restrict certain processing of your data.
      </p>
      
      <h3 className="font-medium text-base mt-4">7. Cookies and Tracking</h3>
      <p>
        We use cookies and similar technologies to enhance your experience, analyze usage patterns, and 
        personalize content. You can control cookies through your browser settings.
      </p>
      
      <h3 className="font-medium text-base mt-4">8. Children's Privacy</h3>
      <p>
        Our services are not intended for individuals under the age of 18. We do not knowingly collect personal 
        information from children under 18.
      </p>
      
      <h3 className="font-medium text-base mt-4">9. International Data Transfers</h3>
      <p>
        Your information may be transferred to and processed in countries other than your own. We ensure 
        appropriate safeguards are in place for such transfers.
      </p>
      
      <h3 className="font-medium text-base mt-4">10. Changes to This Policy</h3>
      <p>
        We may update this Privacy Policy from time to time. We will notify you of significant changes by 
        posting a notice on our website or sending you an email.
      </p>
    </>
  );
};

export default TermsPrivacyDialog;
